import {
  applyToPoint,
  invert,
  localGeometryBounds,
  multiply,
  nodeCommands,
  pinned,
  quadMap,
  ROOT_ID,
  scaleAbout,
  subpathsOfCommands,
  throughQuad,
  translation,
  type Matrix,
  type Pin,
  type Rect,
  type Subpath, pathConversion } from '@flobi/artwork-core';
import type { IconName } from '../chrome/icons';
import { convertedToPath } from './liquify';
import { movable } from './select';
import type { Overlay, Point, RailButton, Tool, ToolContext } from './types';

/**
 * Free Transform and Pin Warp: the two ways of bending a shape rather than moving it.
 *
 * A transform is a matrix, and a matrix can scale, turn and shear and nothing else. Both
 * of these go past it. Free Transform's perspective and distort modes drag the corners of
 * the box independently, which makes parallel lines meet — the thing an affine map cannot
 * do and the thing perspective IS. Pin Warp holds a shape in several places and pulls
 * one of them, which is not a single map at all: every anchor moves by its own amount,
 * depending on which pins it is nearest.
 *
 * Everything here happens WHILE the hand is moving. That sounds obvious and it is the
 * thing this tool got wrong first time round: the box followed the pointer and the shape
 * sat still until the button came up, so every drag was a guess and the result arrived as
 * a surprise. A transform you cannot see while you make it is not a transform tool, it is
 * a dialog box with extra steps.
 *
 * Which means everything is measured from the shape as it was when the drag BEGAN, never
 * from the shape as it is now. Applying a map to an already-mapped shape compounds, and a
 * drag of thirty frames would come out thirty times too far.
 */

type Mode = 'free' | 'puppet';
type Shaping = 'transform' | 'perspective' | 'distort';

const SHAPINGS: { id: Shaping; icon: IconName; title: string }[] = [
  { id: 'transform', icon: 'transform-scale', title: 'Scale — Drag a corner to resize. Drag inside the box to move.' },
  { id: 'perspective', icon: 'transform-perspective', title: 'Perspective — Drag a corner; its partner moves in the opposite direction.' },
  { id: 'distort', icon: 'transform-distort', title: 'Distort — Drag each corner independently to reshape the object.' },
];

/** How near a handle or a pin counts as being, in screen pixels. */
const REACH = 10;
/** How far apart a flat box's handles are spread, in screen pixels (arc-width-warp-F12). */
const FLAT = REACH * 2;
/**
 * The layers a scale gives a new width and height rather than a new matrix, as Select's
 * resize does: the ones whose outline is a box (or a length) at their origin. Text is left
 * out on purpose — a box pulled one way is not a font size — and so are paths, which have
 * no size to give.
 */
const SIZED = new Set(['rect', 'image', 'ellipse', 'polygon', 'star', 'slice', 'effector', 'line']);

type Corners = [Point, Point, Point, Point];

/** The four corners of a rectangle, clockwise from the top left. */
const cornersOf = (box: Rect): Corners => [
  { x: box.x, y: box.y },
  { x: box.x + box.width, y: box.y },
  { x: box.x + box.width, y: box.y + box.height },
  { x: box.x, y: box.y + box.height },
];

/** The corner opposite, and the one across the same edge, for each of the four. */
const ACROSS = [2, 3, 0, 1] as const;
const BESIDE = [1, 0, 3, 2] as const;

const unionOf = (boxes: readonly Rect[]): Rect | null => {
  if (boxes.length === 0) return null;
  let left = Infinity;
  let top = Infinity;
  let right = -Infinity;
  let bottom = -Infinity;
  for (const box of boxes) {
    left = Math.min(left, box.x);
    top = Math.min(top, box.y);
    right = Math.max(right, box.x + box.width);
    bottom = Math.max(bottom, box.y + box.height);
  }
  return { x: left, y: top, width: right - left, height: bottom - top };
};

/**
 * What a shape was when the drag began.
 *
 * A plain scale needs no conversion, so only the matrix (and, for a shape with a size, its
 * box) is remembered and the shape stays whatever it was — a rectangle scaled is still a
 * rectangle, given a new width and height (see `scaledOps`). The two distorting modes have no matrix to write, so the shape is turned into a
 * path once, at the start, and its anchors are rewritten from this every frame after.
 */
type Subject =
  | {
      readonly kind: 'matrix';
      readonly id: string;
      readonly transform: Matrix;
      readonly parent: Matrix;
      /** A layer in SIZED: its outline's box in its own coordinates, and whether it is a line's length. */
      readonly size?: { readonly local: Rect; readonly line: boolean };
    }
  | { readonly kind: 'points'; readonly id: string; readonly outline: Subpath[] }
  /**
   * An artboard being scaled: its box, and where its layers sat, so they can be kept
   * where they are on the page while its origin moves. See `apply`.
   */
  | {
      readonly kind: 'board';
      readonly id: string;
      readonly box: Rect;
      readonly kids: readonly { readonly id: string; readonly transform: Matrix }[];
    };

export function transformTool(): Tool {
  let mode: Mode = 'free';
  let shaping: Shaping = 'transform';

  /**
   * The corners as the hand has dragged them, while a drag is on. `flat` says which way
   * the box had no size before it was opened out for its handles (`handleBox`).
   */
  let held: {
    corner: number | 'inside';
    from: Point;
    was: Corners;
    box: Rect;
    flat: { x: boolean; y: boolean };
    subjects: Subject[];
    live: Corners;
    moved: boolean;
  } | null = null;

  /**
   * Pin warp: the shape as it was before any pin moved, where the pins are, and what the
   * shape looked like when this tool last wrote it — see `puppetGone`.
   */
  let puppet: { id: string; origin: Subpath[]; wrote: string | null } | null = null;
  let pins: Pin[] = [];
  let grabbed: number | null = null;
  let chosen: number | null = null;
  /** The pin-warped shape as it was when the pin under the hand was pressed, for Esc. */
  let beforePin: Subpath[] | null = null;

  const boxOf = (ctx: ToolContext, ids: readonly string[]): Rect | null =>
    unionOf(ids.map((id) => ctx.boundsOf(id)).filter((b): b is Rect => !!b));

  /**
   * What a drag acts on: the selection with every layer that sits inside another chosen
   * one dropped, and nothing locked.
   *
   * A selection box picks a group AND the layers inside it. Moved or scaled one subject
   * per id, the children got the group's change and then their own on top of it — twice
   * as far as the pointer, twice the scale, and left behind by the box (QA 2026-09-25,
   * arc-width-warp-N1). Delete already asked `topmost`; this is the same question.
   */
  const chosenIds = (ctx: ToolContext): string[] => movable(ctx, ctx.selection);

  /**
   * The layers Perspective and Distort actually bend, for what was chosen.
   *
   * Those two rewrite outlines, and a group has none of its own, so a chosen group is
   * taken as the drawn shapes inside it — the visible, unlocked ones a path can hold.
   * Collapsing the selection to its topmost layers without this would have left a
   * box-selected group refused outright (arc-width-warp-N1). Artboards, pictures and text
   * stay as they are and are refused by `asPath`, which says why.
   */
  const bendable = (ctx: ToolContext, ids: readonly string[]): string[] =>
    ids.flatMap((id) => {
      const node = ctx.node(id);
      if (!node || node.type === 'artboard' || pathConversion(ctx.scene, id).ok) return [id];
      const inside = [...ctx.scene.subtree(id)].filter(
        (kid) => kid !== id && pathConversion(ctx.scene, kid).ok && !ctx.scene.lockedHere(kid) && !ctx.scene.hiddenHere(kid),
      );
      return inside.length > 0 ? inside : [id];
    });

  /** A node's outline in world coordinates, whatever it was drawn as. */
  const outlineOf = (ctx: ToolContext, id: string): Subpath[] => {
    const node = ctx.node(id);
    if (!node) return [];
    const local =
      node.type === 'path' ? ((node.path?.subpaths ?? []) as Subpath[]) : subpathsOfCommands([...(nodeCommands(node) ?? [])]);
    const world = ctx.scene.worldTransform(id);
    return local.map((sub) => ({
      closed: sub.closed,
      points: sub.points.map((at) => {
        const on = applyToPoint(world, at.x, at.y);
        const bend = (handle: readonly [number, number] | undefined) => {
          if (!handle) return undefined;
          const moved = applyToPoint(world, handle[0], handle[1]);
          return [moved.x, moved.y] as [number, number];
        };
        return { ...at, x: on.x, y: on.y, in: bend(at.in), out: bend(at.out) };
      }),
    }));
  };

  /**
   * A node turned into a path, once, so the rest of the drag is a `set` rather than a
   * create and a delete every frame.
   *
   * A shape that has been pushed into perspective is not that shape any more — a rectangle
   * dragged into a trapezium has no width and height to speak of — so this is the honest
   * cost of the two distorting modes, paid once at the start where it belongs.
   */
  const asPath = (ctx: ToolContext, id: string): string | null => {
    const node = ctx.node(id);
    if (!node) return null;
    if (node.type === 'path') return id;
    // Everything the layer wears comes with it, in its place in the stack; an artboard,
    // a picture or text is refused instead of being deleted for an empty outline.
    const why = pathConversion(ctx.scene, id);
    if (!why.ok) {
      ctx.notice(why.reason);
      return null;
    }
    const subpaths = subpathsOfCommands([...(nodeCommands(node) ?? [])]);
    if (subpaths.length === 0) return null;
    // Cloners strung along it and effectors shaped by it follow it to its new id, as
    // they do when liquify converts a shape (QA 2026-09-25, liquify-F7).
    return convertedToPath(ctx, id, subpaths);
  };

  /**
   * World-coordinate subpaths written back onto a path node, keeping where it sits.
   *
   * It works out the parent's transform itself rather than being handed one. Being handed
   * one is what broke pin warp: it was given the NODE's world transform instead of its
   * parent's, which this then inverted, so every shape leapt across the page by its own
   * position the moment a pin went down. A matrix nobody can pass in wrongly cannot be
   * passed in wrongly.
   */
  const writePoints = (ctx: ToolContext, id: string, subpaths: Subpath[]): boolean => {
    const op = pointsOp(ctx, id, subpaths);
    return !!op && ctx.edit([op]).ok;
  };

  /** The op `writePoints` runs, so a frame over several shapes can be one edit. */
  const pointsOp = (ctx: ToolContext, id: string, subpaths: Subpath[]): unknown => {
    const parent = ctx.scene.parentOf(id);
    const back = invert(parent ? ctx.scene.worldTransform(parent) : ([1, 0, 0, 1, 0, 0] as Matrix));
    if (!back) return null;
    const local = subpaths.map((sub) => ({
      closed: sub.closed,
      points: sub.points.map((at) => {
        const on = applyToPoint(back, at.x, at.y);
        const bend = (handle: readonly [number, number] | undefined) => {
          if (!handle) return undefined;
          const moved = applyToPoint(back, handle[0], handle[1]);
          return [moved.x, moved.y] as [number, number];
        };
        return { ...at, x: on.x, y: on.y, in: bend(at.in), out: bend(at.out) };
      }),
    }));
    return { op: 'set', id, props: { path: { subpaths: local }, transform: [1, 0, 0, 1, 0, 0] } };
  };

  /** Every point of a shape put through the same map, handles and all. */
  const mapped = (subpaths: readonly Subpath[], move: (at: Point) => Point | null): Subpath[] =>
    subpaths.map((sub) => ({
      closed: sub.closed,
      points: sub.points.map((at) => {
        const on = move({ x: at.x, y: at.y }) ?? { x: at.x, y: at.y };
        const bend = (handle: readonly [number, number] | undefined) => {
          if (!handle) return undefined;
          const moved = move({ x: handle[0], y: handle[1] });
          return moved ? ([moved.x, moved.y] as [number, number]) : undefined;
        };
        return { ...at, x: on.x, y: on.y, in: bend(at.in), out: bend(at.out) };
      }),
    }));

  /**
   * The box the handles go round, and which way it is flat.
   *
   * A perfectly level or upright line has a box with no height or no width, and such a
   * box got no handles at all, so the line could not be taken hold of (QA 2026-09-25,
   * arc-width-warp-F12). It is opened out to FLAT pixels across that way, centred on the
   * line, and nothing is ever scaled that way — `flat` says which.
   */
  const handleBox = (ctx: ToolContext, box: Rect | null): { box: Rect; flat: { x: boolean; y: boolean } } | null => {
    if (!box) return null;
    const flat = { x: !(box.width > 1e-6), y: !(box.height > 1e-6) };
    if (flat.x && flat.y) return null;
    const least = FLAT * ctx.unitsPerPixel;
    return {
      box: {
        x: flat.x ? box.x - least / 2 : box.x,
        y: flat.y ? box.y - least / 2 : box.y,
        width: flat.x ? least : box.width,
        height: flat.y ? least : box.height,
      },
      flat,
    };
  };

  /** Where the corners are right now: dragged, or the selection's own box. */
  const cornersNow = (ctx: ToolContext, corners?: Corners): Corners | null => {
    if (corners) return corners;
    const handles = handleBox(ctx, boxOf(ctx, chosenIds(ctx)));
    return handles ? cornersOf(handles.box) : null;
  };

  const holds = (corners: Corners, at: Point) => {
    const xs = corners.map((c) => c.x);
    const ys = corners.map((c) => c.y);
    return at.x >= Math.min(...xs) && at.x <= Math.max(...xs) && at.y >= Math.min(...ys) && at.y <= Math.max(...ys);
  };

  /**
   * What a press takes hold of: the NEAREST corner within reach, or the inside.
   *
   * The first corner within reach used to win, and on a box under about fourteen pixels
   * every point is within reach of one — so a press in the middle of a small shape resized
   * or flipped it from a corner instead of moving it, and a press on one handle could take
   * another (QA 2026-09-25, arc-width-warp-F13). A press inside the box that is nearer its
   * middle than any handle is a move.
   */
  const grabOf = (corners: Corners, at: Point, reach: number): number | 'inside' | null => {
    let nearest = 0;
    let best = Infinity;
    corners.forEach((corner, index) => {
      const away = Math.hypot(corner.x - at.x, corner.y - at.y);
      if (away < best) {
        best = away;
        nearest = index;
      }
    });
    const inside = holds(corners, at);
    const middle = { x: corners.reduce((sum, c) => sum + c.x, 0) / 4, y: corners.reduce((sum, c) => sum + c.y, 0) / 4 };
    if (best <= reach && !(inside && Math.hypot(middle.x - at.x, middle.y - at.y) < best)) return nearest;
    return inside ? 'inside' : null;
  };

  /**
   * How far a scale goes along one side, from how long the side was and how far the hand
   * pulled it.
   *
   * Never along a way the box is flat (arc-width-warp-F12): a level line has no height to
   * scale. And never to nothing: a drag ending exactly on the opposite edge flattened the
   * shape to no width, and a flat box then had no handles to take it back by
   * (QA 2026-09-25, arc-width-warp-F21). Held to at least one unit across, as Select holds
   * a size to at least 1 — or to the size it already had, if that was less.
   */
  const scaleAlong = (span: number, by: number, flat: boolean): number => {
    if (flat || Math.abs(span) < 1e-6) return 1;
    const scale = (span + by) / span;
    const least = Math.min(1, 1 / Math.abs(span));
    if (Math.abs(scale) >= least) return scale;
    return scale < 0 ? -least : least;
  };

  /**
   * Whether dragged corners still make a box that has not folded over itself: every turn
   * the same way round as the box it started as, and none of them flat.
   *
   * Perspective and Distort stop a corner here. Past it — a corner dragged over the middle
   * or across another — the projective map sends part of the box towards infinity, and
   * the shape ballooned to many times its size or stopped following the box
   * (QA 2026-09-25, arc-width-warp-F14). A convex box always maps to itself.
   */
  const unfolded = (was: Corners, now: Corners): boolean => {
    const turn = (quad: Corners, index: number) => {
      const a = quad[index]!;
      const b = quad[(index + 1) % 4]!;
      const c = quad[(index + 2) % 4]!;
      return (b.x - a.x) * (c.y - b.y) - (b.y - a.y) * (c.x - b.x);
    };
    const start = turn(was, 0);
    const way = Math.sign(start) || 1;
    const least = 1e-6 * Math.abs(start);
    for (let index = 0; index < 4; index++) if (turn(now, index) * way <= least) return false;
    return true;
  };

  /** The corners the drag has reached, from where it started and how far it has come. */
  const draggedTo = (from: Corners, which: number | 'inside', dx: number, dy: number, flat: { x: boolean; y: boolean }): Corners => {
    // Inside the box is a move, which every editor's transform box does and which this one
    // did not: dragging the middle of a selected shape put the selection somewhere else
    // instead of moving it, which is the opposite of what a hand on a shape means.
    if (which === 'inside') return from.map((at) => ({ x: at.x + dx, y: at.y + dy })) as Corners;
    const corners = [...from] as Corners;
    corners[which] = { x: from[which]!.x + dx, y: from[which]!.y + dy };
    if (shaping === 'perspective') {
      // The corner across the same edge mirrors, which is what turns a rectangle into a
      // trapezium rather than into any old quadrilateral.
      const mate = BESIDE[which]!;
      corners[mate] = { x: from[mate]!.x - dx, y: from[mate]!.y - dy };
    } else if (shaping === 'transform') {
      // A scale about the corner opposite, which stays put: the quad stays a rectangle,
      // which is what keeps this an ordinary transform and the shape an ordinary shape.
      const fixed = from[ACROSS[which]!]!;
      const spanX = from[which]!.x - fixed.x;
      const spanY = from[which]!.y - fixed.y;
      const sx = scaleAlong(spanX, dx, flat.x);
      const sy = scaleAlong(spanY, dy, flat.y);
      for (let index = 0; index < 4; index++) {
        corners[index] = { x: fixed.x + (from[index]!.x - fixed.x) * sx, y: fixed.y + (from[index]!.y - fixed.y) * sy };
      }
    }
    return corners;
  };

  /**
   * Puts the shapes where the corners now say, from how they were when the drag began.
   * False when the frame could not be written, so the box stays where the shapes are.
   */
  const apply = (ctx: ToolContext, corners: Corners): boolean => {
    if (!held) return false;
    const { box } = held;
    if (held.corner === 'inside') {
      // A move is a move whichever shaping is chosen: nothing is being distorted, so
      // nothing needs rewriting and every shape keeps what it is.
      const dx = corners[0]!.x - held.was[0]!.x;
      const dy = corners[0]!.y - held.was[0]!.y;
      const ops = held.subjects.flatMap((subject) => {
        const t = subject.kind === 'matrix' ? subject.transform : subject.kind === 'board' ? null : ctx.node(subject.id)?.transform;
        if (!t) return [];
        // How far across the PAGE the hand went, said in the parent's own coordinates: a
        // shape inside a scaled group moves by a different number than the one on screen.
        const parent = ctx.scene.parentOf(subject.id);
        const back = invert(parent ? ctx.scene.worldTransform(parent) : ([1, 0, 0, 1, 0, 0] as Matrix));
        const step = back ? { x: back[0]! * dx + back[2]! * dy, y: back[1]! * dx + back[3]! * dy } : { x: dx, y: dy };
        return [{ op: 'set', id: subject.id, props: { transform: [t[0], t[1], t[2], t[3], t[4]! + step.x, t[5]! + step.y] } }];
      });
      return ops.length === 0 || ctx.edit(ops).ok;
    }
    if (shaping === 'transform') {
      // Nothing is turned into a path: a rectangle scaled is still a rectangle with a
      // width and a height (see `scaledOps`).
      const fixed = held.was[ACROSS[held.corner]!]!;
      const spanX = held.was[held.corner]!.x - fixed.x;
      const spanY = held.was[held.corner]!.y - fixed.y;
      const sx = Math.abs(spanX) < 1e-6 ? 1 : (corners[held.corner]!.x - fixed.x) / spanX;
      const sy = Math.abs(spanY) < 1e-6 ? 1 : (corners[held.corner]!.y - fixed.y) / spanY;
      const scale = scaleAbout(fixed, sx, sy);
      const ops = held.subjects.flatMap((subject) => {
        if (subject.kind === 'board') return boardScaled(subject, fixed, sx, sy);
        if (subject.kind !== 'matrix') return [];
        return scaledOps(subject, scale);
      });
      return ops.length === 0 || ctx.edit(ops).ok;
    }
    // Worked out once for the whole frame rather than once per anchor, which is the
    // difference between a drag that keeps up and one that does not.
    const map = quadMap(corners as unknown as readonly [Point, Point, Point, Point]);
    if (!map) return false;
    const width = box.width || 1;
    const height = box.height || 1;
    // A frame with any point the map cannot place is skipped whole, and so is one the
    // document refuses (a point past its coordinate limit): written anyway, the anchors
    // it could not place stayed put and the curve handles were dropped, so the shape lost
    // its curves and stopped following the box (arc-width-warp-F14).
    let lost = false;
    const move = (at: Point) => {
      const to = throughQuad(map, { x: (at.x - box.x) / width, y: (at.y - box.y) / height });
      if (!to) lost = true;
      return to;
    };
    const ops = held.subjects.flatMap((subject) => {
      if (subject.kind !== 'points') return [];
      const op = pointsOp(ctx, subject.id, mapped(subject.outline, move));
      return op ? [op] : [];
    });
    if (lost) return false;
    return ops.length === 0 || ctx.edit(ops).ok;
  };

  /**
   * A layer scaled on the page, written in its parent's coordinates.
   *
   * The box is upright on the page, so the scale is the page's: it is carried into the
   * parent and back out, as Select's turn is. Put straight onto the layer's matrix in its
   * parent's axes instead, a shape inside a turned group stretched along the group's
   * sides rather than the box's (QA 2026-09-25, arc-width-warp-F20).
   *
   * And a layer with a width and a height is given new ones whenever the scale runs along
   * its own sides, as Select's resize does, so its stroke stays the weight it was all the
   * way round. Written into its matrix, a 4 pt outline stretched three times wider drew
   * 12 pt down the sides (arc-width-warp-F11). Only a scale that runs slantwise across a
   * turned shape stays a matrix, because no width and height can say a shear.
   */
  const scaledOps = (subject: Extract<Subject, { kind: 'matrix' }>, scale: Matrix): unknown[] => {
    const back = invert(subject.parent);
    if (!back) return [];
    const world = multiply(subject.parent, subject.transform);
    const own = subject.size ? invert(world) : null;
    if (subject.size && own) {
      // The page's scale as the layer itself sees it: a plain stretch along its own sides
      // when there is nothing off the diagonal.
      const [a, b, c, d, e, f] = multiply(own, multiply(scale, world));
      const tiny = 1e-9 * Math.max(1, Math.abs(a!), Math.abs(d!));
      if (Math.abs(b!) <= tiny && Math.abs(c!) <= tiny) {
        const { local, line } = subject.size;
        const x = Math.min(a! * local.x + e!, a! * (local.x + local.width) + e!);
        const y = Math.min(d! * local.y + f!, d! * (local.y + local.height) + f!);
        const width = Math.max(1, Math.abs(a!) * local.width);
        const height = Math.max(1, Math.abs(d!) * local.height);
        const transform = multiply(subject.transform, translation(x - local.x, y - local.y));
        return [{ op: 'set', id: subject.id, props: line ? { length: width, transform } : { width, height, transform } }];
      }
    }
    return [{ op: 'set', id: subject.id, props: { transform: multiply(multiply(back, scale), world) } }];
  };

  /** The ops that put every subject of a drag back as it was when the drag began. */
  const restoredOps = (ctx: ToolContext, subjects: readonly Subject[]): unknown[] =>
    subjects.flatMap((subject) => {
      if (subject.kind === 'points') {
        const op = pointsOp(ctx, subject.id, subject.outline);
        return op ? [op] : [];
      }
      if (subject.kind === 'board') {
        const { box } = subject;
        return [
          { op: 'set', id: subject.id, props: { width: box.width, height: box.height, transform: [1, 0, 0, 1, box.x, box.y] } },
          ...subject.kids.map((kid) => ({ op: 'set', id: kid.id, props: { transform: kid.transform }, force: true })),
        ];
      }
      const { size } = subject;
      const sized = size ? (size.line ? { length: size.local.width } : { width: size.local.width, height: size.local.height }) : {};
      return [{ op: 'set', id: subject.id, props: { transform: subject.transform, ...sized } }];
    });

  /**
   * An artboard scaled by the box: a new width and height, never a scale in its matrix.
   *
   * An artboard keeps its size in width and height, and its transform only places it.
   * Written as a matrix, as every other layer is, the board LOOKED bigger while the page
   * panel, its preset and every export still said the old size, and the Artboard tool
   * read its box wrongly after (QA 2026-09-25, arc-width-warp-N2). So it is resized the
   * way the Artboard tool's corner does: its art stays where it is on the page, moved
   * back by however far the board's origin moved. Forced, as there: keeping a locked
   * layer exactly where it is on the page is not an edit to it.
   */
  const boardScaled = (subject: Extract<Subject, { kind: 'board' }>, fixed: Point, sx: number, sy: number): unknown[] => {
    const { box } = subject;
    const xs = [fixed.x + (box.x - fixed.x) * sx, fixed.x + (box.x + box.width - fixed.x) * sx];
    const ys = [fixed.y + (box.y - fixed.y) * sy, fixed.y + (box.y + box.height - fixed.y) * sy];
    const x = Math.round(Math.min(...xs));
    const y = Math.round(Math.min(...ys));
    const width = Math.max(1, Math.round(Math.max(...xs) - Math.min(...xs)));
    const height = Math.max(1, Math.round(Math.max(...ys) - Math.min(...ys)));
    const dx = x - box.x;
    const dy = y - box.y;
    const ops: unknown[] = [{ op: 'set', id: subject.id, props: { width, height, transform: [1, 0, 0, 1, x, y] } }];
    for (const kid of subject.kids) {
      const [a, b, c, d, e, f] = kid.transform;
      ops.push({ op: 'set', id: kid.id, props: { transform: [a, b, c, d, e! - dx, f! - dy] }, force: true });
    }
    return ops;
  };

  /** What a shape looks like, as a string that changes whenever anything moves it. */
  const shapeOf = (ctx: ToolContext, id: string): string | null => {
    const node = ctx.node(id);
    return node ? JSON.stringify([node.type === 'path' ? node.path : null, node.transform]) : null;
  };

  /**
   * Whether the pin-warped shape was changed by something other than this tool, and if
   * so, the pins let go.
   *
   * The pins and the outline they bend live here, not in the document, and every pin
   * move rewrites the whole shape from that outline. So an undo, an arrow-key nudge or a
   * delete left the tool holding a shape that was no longer there: the undone bend came
   * back with the next pin move, a nudge snapped back, and a deleted shape's pins stayed
   * on the canvas doing nothing (QA 2026-09-25, arc-width-warp-F6). The tool is not told
   * about any of those, so it asks — does the shape still look the way it was last left,
   * and is it still the one chosen — and starts over from the shape as it now is if not.
   */
  const puppetGone = (ctx: ToolContext): boolean => {
    if (!puppet) return false;
    if (ctx.selection.includes(puppet.id) && shapeOf(ctx, puppet.id) === puppet.wrote) return false;
    puppet = null;
    pins = [];
    grabbed = null;
    chosen = null;
    return true;
  };

  const startPuppet = (ctx: ToolContext, id: string): boolean => {
    const made = asPath(ctx, id);
    if (!made) return false;
    // The conversion is its own undo step, so taking back the first bend leaves the
    // shape as it was (now a path) instead of taking the shape out from under the pins.
    if (made !== id) ctx.stopCapturing();
    puppet = { id: made, origin: outlineOf(ctx, made), wrote: shapeOf(ctx, made) };
    pins = [];
    grabbed = null;
    chosen = null;
    ctx.setSelection([made]);
    return true;
  };

  const applyPins = (ctx: ToolContext): void => {
    if (!puppet) return;
    // With the last pin gone the shape goes back to the outline the pins bent. Returning
    // early instead left the last pin's pull in place for good (QA 2026-09-25,
    // arc-width-warp-F15); with pins left, it is re-solved from that outline anyway.
    writePoints(ctx, puppet.id, pins.length === 0 ? puppet.origin : mapped(puppet.origin, (at) => pinned(pins, at)));
    puppet.wrote = shapeOf(ctx, puppet.id);
  };

  return {
    id: 'transform',

    /**
     * A drag holds its undo step, so Esc can put everything back inside the same step
     * rather than as a step of its own that undo would take back into the drag again.
     */
    holdsStep: () => held !== null || grabbed !== null,

    deactivate(ctx) {
      held = null;
      puppet = null;
      pins = [];
      grabbed = null;
      chosen = null;
      beforePin = null;
      ctx.setCursor(null);
    },

    onPointerDown(_event, world, ctx) {
      const reach = REACH * ctx.unitsPerPixel;
      if (mode === 'puppet') {
        puppetGone(ctx);
        const found = pins.findIndex((pin) => Math.hypot(pin.to.x - world.x, pin.to.y - world.y) <= reach);
        if (found >= 0) {
          grabbed = found;
          chosen = found;
          beforePin = puppet ? outlineOf(ctx, puppet.id) : null;
          ctx.refresh();
          return true;
        }
        const hit = ctx.hit(world);
        if (!hit) {
          puppet = null;
          pins = [];
          chosen = null;
          ctx.setSelection([]);
          ctx.refresh();
          return true;
        }
        if (!puppet || puppet.id !== hit) {
          if (!startPuppet(ctx, hit)) return true;
        }
        pins.push({ from: { x: world.x, y: world.y }, to: { x: world.x, y: world.y } });
        grabbed = pins.length - 1;
        chosen = grabbed;
        beforePin = puppet ? outlineOf(ctx, puppet.id) : null;
        ctx.refresh();
        return true;
      }

      const corners = cornersNow(ctx);
      const corner = corners ? grabOf(corners, world, reach) : null;
      if (corner === null) {
        // Not on the box at all, so this is a press to choose something. Read the hit
        // rather than the selection, which has not caught up within the same event.
        const hit = ctx.hit(world);
        ctx.setSelection(hit ? [hit] : []);
        ctx.refresh();
        return true;
      }

      // Everything the drag will measure from, taken once: after this the shapes move and
      // measuring from them again would compound every frame into the next. The topmost
      // unlocked layers only, and the box is theirs (arc-width-warp-N1).
      const ids = chosenIds(ctx);
      const handles = handleBox(ctx, boxOf(ctx, ids));
      if (!handles) return true;
      const { box, flat } = handles;
      const subjects: Subject[] = [];
      // A move never distorts, so it never costs a shape its kind — whatever the shaping.
      if (shaping === 'transform' || corner === 'inside') {
        for (const id of ids) {
          const node = ctx.node(id);
          if (!node) continue;
          if (node.type === 'artboard' && corner !== 'inside') {
            const board = node as { width?: number; height?: number };
            subjects.push({
              kind: 'board',
              id,
              box: { x: node.transform[4]!, y: node.transform[5]!, width: board.width ?? 0, height: board.height ?? 0 },
              kids: ctx.scene.childrenOf(id).flatMap((kid) => {
                const transform = ctx.node(kid)?.transform;
                return transform ? [{ id: kid, transform }] : [];
              }),
            });
            continue;
          }
          const parent = ctx.scene.parentOf(id);
          const local = SIZED.has(node.type) ? localGeometryBounds(node) : null;
          subjects.push({
            kind: 'matrix',
            id,
            transform: node.transform,
            parent: parent ? ctx.scene.worldTransform(parent) : [1, 0, 0, 1, 0, 0],
            ...(local ? { size: { local, line: node.type === 'line' } } : {}),
          });
        }
      } else {
        // A layer with no outline to bend — text, a picture, a group of those — stays
        // chosen and untouched, and `asPath` says why. Dropping it from the selection
        // made it look bent-and-lost while the box distorted over nothing
        // (QA 2026-09-25, arc-width-warp-F10).
        const made: string[] = [];
        const kept: string[] = [];
        for (const id of bendable(ctx, ids)) {
          const path = asPath(ctx, id);
          if (path) made.push(path);
          else kept.push(id);
        }
        ctx.setSelection([...made, ...kept]);
        if (made.length === 0) return true;
        for (const id of made) subjects.push({ kind: 'points', id, outline: outlineOf(ctx, id) });
      }
      held = { corner, from: world, was: cornersOf(box), box, flat, subjects, live: cornersOf(box), moved: false };
      return true;
    },

    onPointerMove(event, world, ctx) {
      if (mode === 'puppet') {
        if (event.buttons === 0) {
          if (puppetGone(ctx)) ctx.refresh();
          const reach = REACH * ctx.unitsPerPixel;
          ctx.setCursor(pins.some((pin) => Math.hypot(pin.to.x - world.x, pin.to.y - world.y) <= reach) ? 'grab' : ctx.hit(world) ? 'crosshair' : null);
          return;
        }
        if (grabbed === null || !puppet) return;
        pins[grabbed] = { from: pins[grabbed]!.from, to: { x: world.x, y: world.y } };
        applyPins(ctx);
        ctx.refresh();
        return;
      }
      if (!held) {
        const corners = cornersNow(ctx);
        const over = corners ? grabOf(corners, world, REACH * ctx.unitsPerPixel) : null;
        // Top left and bottom right lean one way, top right and bottom left the other; one
        // cursor for all four pointed along the wrong diagonal on two of them
        // (QA 2026-09-25, arc-width-warp-F29).
        ctx.setCursor(over === null ? null : over === 'inside' ? 'move' : over % 2 === 0 ? 'nwse-resize' : 'nesw-resize');
        return;
      }
      if (event.buttons === 0) return;
      // Live, every frame, from how it was when the drag began. Waiting for the button to
      // come up is what made this tool a guess.
      //
      // The corner lands where a corner should first: a free transform is still somebody
      // saying where an edge goes, and it should agree with the guides on the page. What
      // the drag carries is never something to land on, whatever the selection now says
      // (arc-width-warp-F22).
      const landed = event.metaKey ? world : ctx.place(world, [...new Set([...ctx.selection, ...held.subjects.map((subject) => subject.id)])]);
      const next = draggedTo(held.was, held.corner, landed.x - held.from.x, landed.y - held.from.y, held.flat);
      // Perspective and Distort stop where the box would fold over itself, and a frame
      // that cannot be written is skipped: the box stays with the shapes (arc-width-warp-F14).
      if (held.corner !== 'inside' && shaping !== 'transform' && !unfolded(held.was, next)) return;
      if (!apply(ctx, next)) return;
      held.live = next;
      held.moved = true;
      ctx.refresh();
    },

    onPointerUp(_event, _world, ctx) {
      if (mode === 'puppet') {
        grabbed = null;
        beforePin = null;
        ctx.stopCapturing();
        return;
      }
      // A layer dragged off its artboard goes out onto the page, and one dragged onto
      // another board goes into it, as Select's move does. Left where it was, it sat
      // outside its board's clip and vanished from the canvas while Layers still showed
      // it (QA 2026-09-25, arc-width-warp-F8). Part of the drag's own undo step.
      if (held?.moved) ctx.settleIntoArtboards(held.subjects.map((subject) => subject.id));
      held = null;
      ctx.stopCapturing();
      ctx.refresh();
    },

    onKeyDown(event, ctx) {
      /**
       * Esc during a drag puts it back where it started, as every other editor does. The
       * canvas's Esc only let go of the selection, so the shape went on following the
       * pointer and pin dots stayed on a shape nobody had chosen (QA 2026-09-25,
       * arc-width-warp-F22). A pin warp's pins are let go of as well. With no drag on, Esc
       * is the canvas's.
       */
      if (event.key === 'Escape') {
        if (mode === 'free' && held) {
          event.preventDefault();
          if (held.moved) {
            const ops = restoredOps(ctx, held.subjects);
            if (ops.length > 0) ctx.edit(ops);
          }
          held = null;
        } else if (mode === 'puppet' && grabbed !== null && puppet) {
          event.preventDefault();
          if (beforePin) writePoints(ctx, puppet.id, beforePin);
          puppet = null;
          pins = [];
          grabbed = null;
          chosen = null;
          beforePin = null;
        } else {
          return false;
        }
        ctx.stopCapturing();
        ctx.setCursor(null);
        ctx.refresh();
        return true;
      }
      if (mode !== 'puppet' || (event.key !== 'Backspace' && event.key !== 'Delete')) return false;
      if (puppetGone(ctx) || chosen === null || !pins[chosen]) return false;
      event.preventDefault();
      pins.splice(chosen, 1);
      chosen = null;
      applyPins(ctx);
      ctx.stopCapturing();
      ctx.refresh();
      return true;
    },

    /** Direct choices expose each transform without a second mode selector. */
    rail(ctx) {
      const items: RailButton[] = [
        ...SHAPINGS.map(item => ({
          id: item.id,
          icon: item.icon,
          title: item.title,
          panelGroup: 'Transform',
          pressed: mode === 'free' && shaping === item.id,
          run: () => {
            if (mode === 'free' && shaping === item.id) return;
            mode = 'free';
            shaping = item.id;
            held = null;
            puppet = null;
            pins = [];
            ctx.railChanged();
            ctx.refresh();
          },
        })),
        {
          id: 'puppet',
          icon: 'pin-warp',
          title: 'Pin warp — Click the object to add control points, then drag them to bend it.',
          panelGroup: 'Deform',
          pressed: mode === 'puppet',
          run: () => {
            if (mode === 'puppet') return;
            mode = 'puppet';
            held = null;
            puppet = null;
            pins = [];
            ctx.railChanged();
            ctx.refresh();
          },
        },
      ];
      return { top: { kind: 'buttons' as const, items } };
    },

    /**
     * The box as the QUAD it has been dragged into, rather than the upright rectangle it
     * started as: the whole tool is about the box stopping being a rectangle, and a box
     * that kept drawing itself straight would be showing the one thing no longer true.
     */
    draw(overlay: Overlay, ctx: ToolContext) {
      if (mode === 'puppet') {
        // No pins on a shape that has gone or been changed under them.
        puppetGone(ctx);
        for (const [index, pin] of pins.entries()) {
          if (Math.hypot(pin.to.x - pin.from.x, pin.to.y - pin.from.y) > 1e-6) overlay.guide(pin.from, pin.to);
          overlay.knob(pin.to, { hovered: index === chosen });
        }
        return;
      }
      const at = held ? held.live : cornersNow(ctx);
      if (!at) return;
      overlay.curve([...at, at[0]!].map((corner) => ({ at: corner })), { width: 1 });
      for (const corner of at) overlay.handle(corner, { filled: true });
    },
  };
}
